/**
 * Raid System TypeScript Types
 *
 * Defines all types for WebSocket messages, raid state, and related data structures.
 */

import { Pokemon } from "prisma-layer";
import {
  PlayerRole,
  RaidPlayer,
  RaidPokemon,
  RaidAccumulatedRewards,
  RaidOutcome,
  RaidUsableItem,
  RaidItemUse,
  RaidStatus,
  RaidStepType,
  SlotConfig,
} from "types/raid-dynamodb";
import { RaidRoomConfig, RaidRoomOption } from "./rooms";

export enum RaidDifficulty {
  EASY = "easy",
  NORMAL = "normal",
  HARD = "hard",
  EXPERT = "expert",
  EPIC = "epic",
}

// ============================================================================
// WebSocket Message Types - Client to Server
// ============================================================================

export interface PlayerActionMessage {
  action: "player_action";
  payload:
    | PlayerActionPokemonPickPayload
    | PlayerActionPokemonPickCancelPayload
    | PlayerActionSubmitPicksPayload
    | PlayerActionRoomVotePayload
    | PlayerActionItemUsePayload;
}

export interface PlayerActionPokemonPickPayload {
  __typename: "PlayerActionPokemonPickPayload";
  raid_id: string;
  round_index: number;
  slot_index: number;
  pokemon_id: number;
}

export interface PlayerActionPokemonPickCancelPayload {
  __typename: "PlayerActionPokemonPickCancelPayload";
  raid_id: string;
  round_index: number;
  pokemon_id: number;
}

/** Payload for submitting final pokemon picks (locking in selection) */
export interface PlayerActionSubmitPicksPayload {
  __typename: "PlayerActionSubmitPicksPayload";
  raid_id: string;
  round_index: number;
  /** Pokemon IDs the player is contributing to the battle team */
  pokemon_ids: number[];
}

export interface PlayerActionRoomVotePayload {
  __typename: "PlayerActionRoomVotePayload";
  raid_id: string;
  round_index: number;
  /** Index of the room option (0-3) */
  room_index: number;
}

export interface PlayerActionItemUsePayload {
  __typename: "PlayerActionItemUsePayload";
  raid_id: string;
  round_index: number;
  /** Item name to use */
  item_name: RaidUsableItem;
  /** Target pokemon ID (can be any player's pokemon) */
  target_pokemon_id: number;
}

export interface ReadyMessage {
  action: "ready";
  payload: {
    raid_id: string;
    is_ready: boolean;
  };
}

export interface VisualAckMessage {
  action: "visual_ack";
  payload: {
    raid_id: string;
    round_index: number;
  };
}

export interface GetStateMessage {
  action: "get_state";
  payload: {
    raid_id: string;
  };
}

export interface HeartbeatMessage {
  action: "heartbeat";
}

export interface StartRaidMessage {
  action: "start_raid";
  payload: {
    raid_id: string;
  };
}

export interface QuitRaidMessage {
  action: "quit_raid";
  payload: {
    raid_id: string;
  };
}

export interface LeaveLobbyMessage {
  action: "leave_lobby";
  payload: {
    raid_id: string;
  };
}

export interface SetDifficultyMessage {
  action: "set_difficulty";
  payload: {
    raid_id: string;
    difficulty: RaidDifficulty;
  };
}

export type ClientMessage =
  | PlayerActionMessage
  | VisualAckMessage
  | GetStateMessage
  | HeartbeatMessage
  | StartRaidMessage
  | QuitRaidMessage
  | LeaveLobbyMessage
  | ReadyMessage
  | SetDifficultyMessage;

// ============================================================================
// WebSocket Message Types - Server to Client
// ============================================================================

/** Sent when a player joins the lobby */
export interface RaidPlayerJoinedMessage {
  type: "player_joined";
  payload: {
    raid_id: string;
    player_id: string;
    player_role: PlayerRole;
    player_name: string;
    player_country: string | null;
    pokemon_team: Array<{
      display_id: string;
      base_pokemon_name: string;
      level: number;
    }>;
  };
}

/** Sent when a player leaves the lobby */
export interface RaidPlayerLeftMessage {
  type: "player_left";
  payload: {
    raid_id: string;
    player_id: string;
    player_role: PlayerRole;
  };
}

/** Sent when a player toggles ready state in lobby */
export interface RaidPlayerReadyMessage {
  type: "player_ready";
  payload: {
    raid_id: string;
    player_id: string;
    player_role: PlayerRole;
    is_ready: boolean;
  };
}

/** Sent when host changes in lobby */
export interface RaidHostChangedMessage {
  type: "host_changed";
  payload: {
    raid_id: string;
    new_host_id: string;
  };
}

/** Sent when host changes difficulty in lobby */
export interface RaidDifficultyChangedMessage {
  type: "difficulty_changed";
  payload: {
    raid_id: string;
    difficulty: RaidDifficulty;
  };
}

/** Sent when host changes raid type in lobby */
export interface RaidTypeUpdateMessage {
  type: "raid_type_update";
  payload: {
    raid_id: string;
    raid_name: string;
  };
}

/** Sent when raid starts (all players ready, host clicked start) */
export interface RaidStartedMessage {
  type: "raid_started";
  payload: {
    raid_id: string;
    raid_name: string;
    difficulty: RaidDifficulty;
    your_role: PlayerRole;
    players: {
      player_a: RaidPlayerInfo | null;
      player_b: RaidPlayerInfo | null;
      player_c: RaidPlayerInfo | null;
      player_d: RaidPlayerInfo | null;
    };
  };
}

/** Player info sent in raid_started message */
export interface RaidPlayerInfo {
  id: string;
  username: string;
  country: string | null;
  team: {
    name: string;
    pokemon: RaidPokemon[];
  };
  is_ready: boolean;
}

/** Sent when a new round begins */
export interface RaidRoundStartedMessage {
  type: "round_started";
  payload: {
    round_index: number;
    is_boss_round: boolean;
  };
}

/** Sent when room selection phase starts */
export interface RoomSelectionStartedMessage {
  type: "room_selection_started";
  payload: {
    round_index: number;
    duration_seconds: number;
    room_options: RaidRoomOption[];
    started_at: string;
  };
}

/** Sent when a player votes for a room */
export interface RoomVoteUpdateMessage {
  type: "room_vote_update";
  payload: {
    round_index: number;
    player_role: PlayerRole;
    room_index: number;
  };
}

/** Sent when room selection completes */
export interface RoomSelectionResultMessage {
  type: "room_selection_result";
  payload: {
    round_index: number;
    selected_room: RaidRoomOption;
    vote_counts: number[];
  };
}

/** Sent when pokemon draft phase starts */
export interface DraftStartedMessage {
  type: "draft_started";
  payload: {
    round_index: number;
    duration_seconds: number;
    picks_required: number;
    slots: SlotConfig[];
    /** All available pokemon from all players with current HP (non-fainted) */
    available_pokemon: RaidPokemon[];
    /** The room that was selected for this round */
    selected_room: RaidRoomOption;
    started_at: string;
  };
}

/** Sent when a player picks/unpicks a pokemon during draft */
export interface DraftPickUpdateMessage {
  type: "draft_pick_update";
  payload: {
    round_index: number;
    slot_index: number;
    player_role: PlayerRole;
    pokemon_id: number | null;
  };
}

/** Sent when a player uses an item during draft */
export interface ItemUsedMessage {
  type: "item_used";
  payload: {
    round_index: number;
    player_role: PlayerRole;
    item_name: RaidUsableItem;
    target_pokemon_id: number;
    /** New HP after item use (for healing items) */
    new_hp?: number;
    /** Whether pokemon was revived */
    was_revived?: boolean;
  };
}

/** Sent when draft phase completes */
export interface DraftResultMessage {
  type: "draft_result";
  payload: {
    round_index: number;
    /** Final team pokemon IDs for battle */
    final_team: number[];
  };
}

/** Sent when battle is about to start */
export interface BattleStartingMessage {
  type: "battle_starting";
  payload: {
    round_index: number;
  };
}

/** Sent when battle starts with log URL */
export interface BattleStartedMessage {
  type: "battle_started";
  payload: {
    round_index: number;
    battle_log_url: string;
  };
}

/** Sent when battle completes */
export interface BattleResultMessage {
  type: "battle_result";
  payload: {
    round_index: number;
    outcome: RaidOutcome;
    /** Updated pokemon HP after battle */
    pokemon_hp: Record<number, number>;
    /** Pokemon that fainted this battle */
    fainted_pokemon: number[];
  };
}

/** Sent when raid ends successfully */
export interface RaidVictoryMessage {
  type: "raid_victory";
  payload: {
    /** Total coins earned */
    coins_earned: number;
    /** Experience distributed to pokemon */
    experience_earned: number;
    /** Accumulated rewards from rooms */
    rewards: RaidAccumulatedRewards;
    /** Boss-specific drops (e.g., Mega Stones) */
    boss_drops?: string[];
  };
}

/** Sent when raid ends in defeat */
export interface RaidDefeatMessage {
  type: "raid_defeat";
  payload: {
    /** Reduced coins earned (~30%) */
    coins_earned: number;
    /** Reduced experience earned (~30%) */
    experience_earned: number;
    /** Round where defeat occurred */
    defeat_round: number;
  };
}

/** Sent when a player quits, ending the raid for everyone */
export interface RaidAbandonedMessage {
  type: "raid_abandoned";
  payload: {
    /** Player who quit */
    quitter_role: PlayerRole;
    quitter_name: string;
  };
}

export interface ErrorMessage {
  type: "error";
  payload: {
    code: string;
    message: string;
  };
}

export interface ConnectionStatusMessage {
  type: "connection_status";
  payload: {
    status: "connected" | "reconnected" | "player_disconnected";
    /** Which player disconnected (if status is player_disconnected) */
    disconnected_player?: PlayerRole;
  };
}

/** Sent when player connects/reconnects to get current raid state */
export interface RaidStateMessage {
  type: "raid_state";
  payload: {
    raid_id: string;
    raid_name: string;
    player_role: PlayerRole;
    status: RaidStatus;
    current_round_index: number;
    current_step: RaidStepType;
    host_id: string;
    players: {
      player_a: RaidPlayer | null;
      player_b: RaidPlayer | null;
      player_c: RaidPlayer | null;
      player_d: RaidPlayer | null;
    };
  };
}

export type ServerMessage =
  | RaidPlayerJoinedMessage
  | RaidPlayerLeftMessage
  | RaidPlayerReadyMessage
  | RaidHostChangedMessage
  | RaidDifficultyChangedMessage
  | RaidTypeUpdateMessage
  | RaidStartedMessage
  | RaidRoundStartedMessage
  | RoomSelectionStartedMessage
  | RoomVoteUpdateMessage
  | RoomSelectionResultMessage
  | DraftStartedMessage
  | DraftPickUpdateMessage
  | ItemUsedMessage
  | DraftResultMessage
  | BattleStartingMessage
  | BattleStartedMessage
  | BattleResultMessage
  | RaidVictoryMessage
  | RaidDefeatMessage
  | RaidAbandonedMessage
  | RaidStateMessage
  | ErrorMessage
  | ConnectionStatusMessage;

// ============================================================================
// WebSocket Service Types
// ============================================================================

export type WebSocketConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting";

export interface WebSocketConfig {
  url: string;
  raid_id: string;
  player_id: string;
  token: string;
  reconnectAttempts?: number;
  reconnectDelay?: number;
  heartbeatInterval?: number;
}

export type MessageHandler = (message: ServerMessage) => void;
export type ConnectionHandler = (status: WebSocketConnectionStatus) => void;
export type ErrorHandler = (error: Error) => void;
